import { useEffect, useRef } from "react";

/**
 * True when a pointer hit lands on an element's scrollbar track rather than its
 * content. offsetX/offsetY are relative to the target's padding box, so a hit
 * beyond the content box (clientWidth/clientHeight) is on the scrollbar.
 */
export function isScrollbarClick({
  offsetX,
  offsetY,
  clientWidth,
  clientHeight,
}: {
  offsetX: number;
  offsetY: number;
  clientWidth: number;
  clientHeight: number;
}): boolean {
  return offsetX > clientWidth || offsetY > clientHeight;
}

/**
 * HeadlessUI `Dialog` closes on outside-click, which includes clicking the
 * scrollbar of a scroll container rendered outside the `DialogPanel`. Its
 * `onClose` receives no event, so the close handler cannot inspect the target.
 *
 * This hook tracks scrollbar pointerdowns at the DOM-event layer and returns a
 * guarded close handler that ignores them. Pass the result as `Dialog`'s
 * `onClose`; backdrop clicks and Escape still close normally.
 */
export function useIgnoreScrollbarClose({
  open,
  onClose,
}: {
  open: boolean;
  onClose: () => void;
}): () => void {
  const isScrollbarPointerDownRef = useRef(false);

  useEffect(() => {
    if (!open) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target;
      isScrollbarPointerDownRef.current =
        target instanceof HTMLElement &&
        isScrollbarClick({
          offsetX: event.offsetX,
          offsetY: event.offsetY,
          clientWidth: target.clientWidth,
          clientHeight: target.clientHeight,
        });
    };

    // Keyboard-driven closes (Escape) and aborted pointer gestures must never
    // be blocked by a stale flag from a scrollbar pointerdown that never
    // produced a close (pointercancel, prevented pointerup, mobile touch).
    // window capture runs before HeadlessUI's document-level Escape handler.
    const clearScrollbarFlag = () => {
      isScrollbarPointerDownRef.current = false;
    };

    // Capture phase so the flag is set before HeadlessUI resolves the close.
    document.addEventListener("pointerdown", handlePointerDown, true);
    document.addEventListener("pointercancel", clearScrollbarFlag, true);
    window.addEventListener("keydown", clearScrollbarFlag, true);

    return () => {
      document.removeEventListener("pointerdown", handlePointerDown, true);
      document.removeEventListener("pointercancel", clearScrollbarFlag, true);
      window.removeEventListener("keydown", clearScrollbarFlag, true);
    };
  }, [open]);

  return () => {
    if (isScrollbarPointerDownRef.current) {
      isScrollbarPointerDownRef.current = false;

      return;
    }

    onClose();
  };
}
